import React from 'react';
import NextHead from 'next/head';
import { useRouter } from 'next/router';
import ProptTypes from 'prop-types';
import queryString from 'utils/queryString';
import { buildOrganizationJson } from 'components/rich';

import SETTINGS from '../../settings';

const HEAD_KEY = {
  CHAR_SET: 'charSet',
  HTTP_EQUIV_COMPATIBLE: 'httpEquivCompatible',
  HTTP_EQUIV_ACCEPT: 'httpEquivAccept',
  VIEWPORT: 'viewport',
  TITLE: 'title',
  DESCRIPTION: 'description',
  ICON: 'icon',
  TWITTER_IMAGE: 'twitterImage',
  TWITTER_CARD: 'twitterCard',
  TWITTER_SITE: 'twitterSite',
  TWITTER_TITLE: 'twitterTitle',
  TWITTER_DESCRIPTION: 'twitterDescription',
  TWITTER_CREATOR: 'twitterCreator',
  OG_SITE: 'ogSite',
  OG_TITLE: 'ogTitle',
  OG_TYPE: 'ogType',
  OG_URL: 'ogUrl',
  OG_IMAGE: 'ogImage',
  OG_DESCRIPTION: 'ogDescription',
  CANONICAL: 'canonical',
  FB_VERIFICATION: 'fbVerification',
  HERO_PRELOAD: 'heroPreload',
  ROBOTS: 'robots',
  APPLE_ITUNES_APP: 'appleItunesApp',
};

const ORGANIZATION = {
  name: 'Parabo Press',
  description:
    'Parabo Press is an American photo printing company creating thoughtfully designed prints, books, and decor.',
  logo: '/email/ParaboLogoBlack-JBH.png',
  foundingDate: '2015',
  address: {
    addressLocality: 'Philadelphia',
    addressRegion: 'PA',
    addressCountry: 'US',
  },
  email: 'help@parabo.press',
  sameAs: [
    SETTINGS.X_URL,
    SETTINGS.FACEBOOK_URL,
    SETTINGS.INSTAGRAM_URL,
    SETTINGS.PINTEREST_URL,
    'https://www.linkedin.com/company/parabo-press',
    'https://www.youtube.com/channel/UCo3RYv-7o6JfRDaMbzsXgAg',
    'https://www.crunchbase.com/organization/parabo-press',
    'https://www.brit.co/best-photo-sharing-and-printing-sites-new-parents/',
    'https://petapixel.com/2015/10/07/parabo-press-a-new-photo-printing-app-by-photojojo-for-unique-small-batch-prints/',
    'https://thenextweb.com/news/photojojo-launches-parabo-press-ios-app-for-stylish-photo-prints',
  ],
};

const Head = props => {
  const router = useRouter();

  const pageUrl = `${SETTINGS.HOST}${queryString.parseUrl(router.asPath).url}`;
  const canonicalUrl = props.canonicalPath ? `${SETTINGS.HOST}${props.canonicalPath}` : pageUrl;
  const fullTitle = props.fullTitle ?? `${props.title} | ${props.siteName}`;
  // Link unfurlers (Slack) show the escaped &amp; literally, so share titles spell it out.
  const shareTitle = fullTitle.replaceAll(' & ', ' and ');
  const organizationJson = buildOrganizationJson({
    ...ORGANIZATION,
    host: SETTINGS.HOST,
  });

  return (
    <NextHead>
      <meta key={HEAD_KEY.CHAR_SET} charSet="utf-8" />
      <meta key={HEAD_KEY.HTTP_EQUIV_COMPATIBLE} httpEquiv="X-UA-Compatible" content="IE=edge" />
      <meta
        key={HEAD_KEY.HTTP_EQUIV_ACCEPT}
        httpEquiv="Accept-CH"
        content="DPR, Viewport-Width, Width"
      />
      <meta
        key={HEAD_KEY.VIEWPORT}
        name="viewport"
        content="width=device-width, initial-scale=1"
      />

      <title key={HEAD_KEY.TITLE}>{fullTitle}</title>
      <meta key={HEAD_KEY.DESCRIPTION} name="description" content={props.description} />
      <link key={HEAD_KEY.ICON} rel="icon" href="/static/favicon.png" />
      <link key={HEAD_KEY.CANONICAL} rel="canonical" href={canonicalUrl} />

      {props.noindex && (
        <meta key={HEAD_KEY.ROBOTS} name="robots" content="noindex, follow" />
      )}

      {props.heroImage && (
        // imageSrcSet/imageSizes must mirror the rendered hero <source> exactly, otherwise the
        // browser preloads one candidate and downloads another (double fetch at DPR >= 2).
        // href stays as a 1x fallback for browsers without imagesrcset support — supporting
        // ones ignore it when imageSrcSet is present.
        <link
          key={HEAD_KEY.HERO_PRELOAD}
          rel="preload"
          as="image"
          href={props.heroImage}
          imageSrcSet={props.heroImageSrcSet}
          imageSizes={props.heroImageSrcSet ? props.heroImageSizes : undefined}
          type="image/webp"
          fetchPriority="high"
        />
      )}

      <meta key={HEAD_KEY.TWITTER_IMAGE} name="twitter:image" content={props.image} />
      <meta key={HEAD_KEY.TWITTER_CARD} name="twitter:card" content="summary_large_image" />
      <meta key={HEAD_KEY.TWITTER_SITE} name="twitter:site" content="@parabopress" />
      <meta
        key={HEAD_KEY.TWITTER_TITLE}
        name="twitter:title"
        content={shareTitle}
      />
      <meta
        key={HEAD_KEY.TWITTER_DESCRIPTION}
        name="twitter:description"
        content={props.description}
      />

      <meta key={HEAD_KEY.OG_SITE} property="og:site_name" content={props.siteName} />
      <meta
        key={HEAD_KEY.OG_TITLE}
        property="og:title"
        content={shareTitle}
      />
      <meta key={HEAD_KEY.OG_TYPE} property="og:type" content={props.ogType || "website"} />
      <meta key={HEAD_KEY.OG_URL} property="og:url" content={canonicalUrl} />
      <meta key={HEAD_KEY.OG_IMAGE} property="og:image" content={props.image} />
      <meta key={HEAD_KEY.OG_DESCRIPTION} property="og:description" content={props.description} />

      {/* Safari on iOS renders the native Smart App Banner from this tag, other browsers ignore it. */}
      <meta
        key={HEAD_KEY.APPLE_ITUNES_APP}
        name="apple-itunes-app"
        content={`app-id=${SETTINGS.APP_STORE_ID}`}
      />

      <meta
        key={HEAD_KEY.FB_VERIFICATION}
        name="facebook-domain-verification"
        content="96v9vbu83mhdhhbjofxeaa8rvyupng"
      />

      <script
        key="ScriptMarkupOrganization"
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: organizationJson }}
      />
    </NextHead>
  );
};

Head.propTypes = {
  siteName: ProptTypes.string,
  title: ProptTypes.string,
  // Whole <title> as written, without the site name suffix.
  fullTitle: ProptTypes.string,
  // Path of another URL this page defers to, e.g. a thin sibling of a hub product.
  canonicalPath: ProptTypes.string,
  description: ProptTypes.string,
  image: ProptTypes.string,
  ogType: ProptTypes.string,
  heroImage: ProptTypes.string,
  // Candidate list + sizes of the rendered hero element (width or DPR descriptors).
  heroImageSrcSet: ProptTypes.string,
  heroImageSizes: ProptTypes.string,
  noindex: ProptTypes.bool,
};

Head.defaultProps = {
  siteName: 'Parabo Press',
  title: 'Order Photo Prints, Books, Cards & Display',
  description:
    'Our online photo printing service brings digital memories to life with thoughtfully-designed photo prints, books, wall art and décor.',
  image:
    'https://templates.parabo.press/square-print-small/4.0.0/product_images/web/homepage.jpg',
};

export default Head;
